import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Typography,
  Alert
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Lock as LockIcon,
} from '@mui/icons-material';

const CopyDialog = ({
  open,
  onClose,
  item,
  file, // Alternative prop name for item
  sourceArea,
  currentStorageArea, // Alternative prop name for sourceArea
  onCopy,
  loading = false,
  error = null
}) => {
  const [selectedDestination, setSelectedDestination] = useState(null);

  // Support both prop naming conventions
  const displayItem = item || file;
  const currentArea = sourceArea || currentStorageArea;

  const handleCopy = () => {
    if (!selectedDestination) return;
    onCopy(selectedDestination);
  };

  const handleClose = () => {
    setSelectedDestination(null);
    onClose();
  };

  // Show all destinations except the current storage area
  const allDestinations = [
    {
      id: 'personal',
      name: 'Personal Documents',
      description: 'Only visible to you',
      icon: <PersonIcon />,
    },
    {
      id: 'family',
      name: 'Family Documents',
      description: 'Visible to all family members',
      icon: <GroupIcon />,
    },
    {
      id: 'vault',
      name: 'Family Vault',
      description: 'Secure storage with access controls',
      icon: <LockIcon />,
    }
  ];

  const destinations = allDestinations.filter(d => d.id !== currentArea);

  return (
    <Dialog
      open={open}
      onClose={!loading ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Copy "{displayItem?.name}" to...
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose where to copy this file:
        </Typography>
        <List>
          {destinations.map((option) => (
            <ListItemButton
              key={option.id}
              selected={selectedDestination === option.id}
              onClick={() => setSelectedDestination(option.id)}
              data-testid={`destination-${option.id}`}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                border: selectedDestination === option.id ? '2px solid' : '1px solid',
                borderColor: selectedDestination === option.id ? 'primary.main' : 'divider',
              }}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText
                primary={option.name}
                secondary={option.description}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleCopy}
          color="primary"
          variant="contained"
          disabled={!selectedDestination || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Copying...' : 'Copy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyDialog;
